import React, { useEffect, useState, useRef } from 'react';
import { PhoneIcon, PauseIcon, PlayIcon, PhoneOffIcon, ArrowUpDownIcon, ChevronDownIcon } from 'lucide-react';
import { LaneType, Lead, Loan, Retention, AnyContact, SortOption } from '../types';
interface MetricsStripProps {
  laneType: LaneType;
  data: any[];
  allData?: {
    leads: Lead[];
    loans: Loan[];
    retention: Retention[];
  };
  onPowerDial: () => void;
  isPowerDialing: boolean;
  isPaused?: boolean;
  onPauseToggle?: () => void;
  onEndSession?: () => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
}
export function MetricsStrip({
  laneType,
  data,
  allData,
  onPowerDial,
  isPowerDialing,
  isPaused = false,
  onPauseToggle,
  onEndSession,
  sortBy,
  onSortChange
}: MetricsStripProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const today = new Date().toISOString().split('T')[0];
  const getContacts = (): AnyContact[] => {
    if (laneType === 'all' && allData) {
      return [...allData.leads, ...allData.loans, ...allData.retention];
    }
    return data;
  };
  const contacts = getContacts();
  const totalContacts = contacts.length;
  const newToday = contacts.filter(c => {
    if (c.type === 'lead') return (c as Lead).daysSinceContact === 0;
    if (c.type === 'loan') return (c as Loan).daysInPipeline <= 1;
    return false;
  }).length;
  const followUpsToday = contacts.filter(c => c.scheduledFollowUp === today).length;
  const renderMetrics = () => {
    if (totalContacts === 0) {
      return <div className="text-olympian-textMuted text-sm flex items-center h-full" data-id="element-258">
          No contacts in this lane.
        </div>;
    }
    return <>
        <MetricItem value={totalContacts} label="Total Contacts" data-id="element-259" />
        <MetricDivider data-id="element-260" />
        <MetricItem value={newToday} label="New Today" valueColor="text-cyan-400" data-id="element-261" />
        <MetricDivider data-id="element-262" />
        <MetricItem value={followUpsToday} label="Follow-Ups Today" valueColor={followUpsToday > 0 ? 'text-olympian-gold' : 'text-theme'} data-id="element-263" />
      </>;
  };
  const titles: Record<LaneType, {
    title: string;
    sub: string;
  }> = {
    all: {
      title: 'All Contacts',
      sub: 'Complete pipeline overview'
    },
    leads: {
      title: 'Follow-Up Queue',
      sub: 'Unconverted leads & pre-doc loans'
    },
    loans: {
      title: 'In-Progress Loans',
      sub: 'Awaiting documents & signatures'
    },
    retention: {
      title: 'Retention & Repeat',
      sub: 'Funded loans for re-engagement'
    }
  };
  const getSortOptions = (): {
    value: SortOption;
    label: string;
  }[] => {
    if (laneType === 'all') return [{
      value: 'followUp',
      label: 'Follow-Up Date'
    }, {
      value: 'overdue',
      label: 'Most Overdue'
    }, {
      value: 'name',
      label: 'Name (A-Z)'
    }];
    if (laneType === 'leads') return [{
      value: 'followUp',
      label: 'Follow-Up Date'
    }, {
      value: 'daysSinceContact',
      label: 'Days Since Contact'
    }, {
      value: 'name',
      label: 'Name (A-Z)'
    }];
    if (laneType === 'loans') return [{
      value: 'followUp',
      label: 'Follow-Up Date'
    }, {
      value: 'daysInPipeline',
      label: 'Days in Pipeline'
    }, {
      value: 'docsRemaining',
      label: 'Docs Remaining'
    }, {
      value: 'name',
      label: 'Name (A-Z)'
    }];
    if (laneType === 'retention') return [{
      value: 'followUp',
      label: 'Follow-Up Date'
    }, {
      value: 'originalLoanDate',
      label: 'Original Loan Date'
    }, {
      value: 'name',
      label: 'Name (A-Z)'
    }];
    return [];
  };
  const sortOptions = getSortOptions();
  const currentSortLabel = sortOptions.find(o => o.value === sortBy)?.label || 'Sort';
  return <div className="h-[72px] bg-olympian-surface border-b border-olympian-border flex items-center justify-between px-6 shrink-0" data-id="element-264">
      <div className="flex items-center h-full gap-8" data-id="element-265">
        <div className="min-w-[180px]" data-id="element-266">
          <h1 className="text-base font-bold text-theme leading-tight" data-id="element-267">
            {titles[laneType].title}
          </h1>
          <p className="text-[11px] text-olympian-textMuted mt-0.5" data-id="element-268">
            {titles[laneType].sub}
          </p>
        </div>
        <div className="h-8 w-px bg-olympian-border" data-id="element-269" />
        <div className="flex items-center h-full py-4" data-id="element-270">{renderMetrics()}</div>
      </div>

      <div className="flex items-center gap-4 shrink-0" data-id="element-271">
        {!isPowerDialing && <div className="relative" ref={sortRef} data-id="element-272">
            <button onClick={() => setSortOpen(!sortOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-olympian-border bg-olympian-surface/50 hover:bg-olympian-surface transition-colors text-sm text-theme" data-id="element-273">
              <ArrowUpDownIcon className="w-3.5 h-3.5 text-olympian-textMuted" data-id="element-274" />
              <span className="font-medium" data-id="element-275">{currentSortLabel}</span>
              <ChevronDownIcon className="w-3.5 h-3.5 text-olympian-textMuted ml-1" data-id="element-276" />
            </button>

            {sortOpen && <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-olympian-border bg-olympian-surface shadow-xl z-50 overflow-hidden animate-crossfade" data-id="element-277">
                {sortOptions.map(option => <button key={option.value} onClick={() => {
            onSortChange(option.value);
            setSortOpen(false);
          }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sortBy === option.value ? 'bg-olympian-gold/10 text-olympian-gold font-semibold' : 'text-theme hover:bg-white/5'}`} data-id="element-278">
                    {option.label}
                  </button>)}
              </div>}
          </div>}

        <div className="flex items-center gap-2 shrink-0" data-id="element-279">
          {isPowerDialing ? <>
              <button onClick={onPauseToggle} className={`px-5 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all border ${isPaused ? 'bg-olympian-gold text-olympian-navy hover:bg-olympian-goldHover border-transparent shadow-[0_0_12px_rgba(212,168,67,0.25)]' : 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20'}`} data-id="element-280">
                {isPaused ? <PlayIcon className="w-4 h-4" data-id="element-281" /> : <PauseIcon className="w-4 h-4" data-id="element-282" />}
                {isPaused ? 'Resume Dialing' : 'Pause Dialing'}
              </button>
              <button onClick={onEndSession} className="px-5 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20" data-id="element-283">
                <PhoneOffIcon className="w-4 h-4" data-id="element-284" />
                End Session
              </button>
            </> : <button onClick={onPowerDial} disabled={data.length === 0} className={`px-5 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all shrink-0
                ${data.length === 0 ? 'bg-olympian-navyLight text-olympian-textMuted cursor-not-allowed border border-olympian-border' : 'bg-olympian-gold text-olympian-navy hover:bg-olympian-goldHover shadow-[0_0_12px_rgba(212,168,67,0.25)]'}`} data-id="element-285">
              <PhoneIcon className="w-4 h-4" data-id="element-286" />
              Power Dial
            </button>}
        </div>
      </div>
    </div>;
}
function MetricItem({
  value,
  label,
  valueColor = 'text-theme'
}: {
  value: string | number;
  label: string;
  valueColor?: string;
}) {
  return <div className="flex flex-col justify-center px-4" data-id="element-287">
      <span className={`text-xl font-mono font-semibold ${valueColor} leading-none mb-1`} data-id="element-288">
        {value}
      </span>
      <span className="text-[10px] text-olympian-textMuted uppercase tracking-wider font-semibold whitespace-nowrap" data-id="element-289">
        {label}
      </span>
    </div>;
}
function MetricDivider() {
  return <div className="w-px h-7 bg-olympian-border" data-id="element-290" />;
}